//@ts-strict-ignore
const React = require('react');

import { isCtrlKeyHeldDown, isKeyPrintable, isShiftKeyHeldDown } from '../utils/keyboardUtils';
import getIn from '../../connectedForm/utils/getIn';
import isFunction, { isReactMemoOrLazy } from '../utils/isFunction';

const joinClasses = require('classnames');
const SimpleTextEditor = require('./SimpleTextEditor');

require('styles/react-data-grid/react-data-grid-core.css');

export type CommitObject = {
  cellKey: string;
  cellKey2: string;
  rowIdx: number;
  updated: any;
  key: any;
  shift: boolean;
  searchTabOffset: any;
  searchEnterOffset: any;
  setInactive: any;
  triggerTemporarySave: any;
};

type IEditorCommitFunction = (
  args: { key: string; shift?: boolean },
  options: { value: any; skipTabBehaviour: boolean; setInactive: boolean; triggerTemporarySave: boolean }
) => void;

export type EditorProps = {
  ref?: any;
  column: { editable: boolean; key: string; name: string; options: any };
  value: any;
  onCommit: IEditorCommitFunction;
  onCommitCancel: () => void;
  rowMetaData: any;
  rowData: any;
  height: number;
  onBlur: IEditorCommitFunction;
  onOverrideKeyDown: (e: any) => void;
  testId: string;
  updateCellState: any;
  modalOpen: boolean;
  rowIdx: number;
};

type IEditorContainerProps = {
  rowIdx: number;
  rowData: any;
  value: string | number | boolean | any;
  cellMetaData: {
    selected: any;
    copied?: any;
    dragged?: any;
    onCellClick?: Function;
    onCellDoubleClick?: Function;
    onCommitCancel?: Function;
    onCommit?: Function;
  };
  column: {
    options?: {
      linkedFields?: any;
      valueMap?: any;
    };
  };
  height: number;
  onGridKeyDown?: Function;
};

class EditorContainer extends React.Component<IEditorContainerProps> {
  static displayName = 'EditorContainer';

  changeCommitted = false;
  changeCanceled = false;
  editorRef?: {
    disableContainerStyles?: boolean;
    ignore?: boolean;
    hasResults?: any;
    isSelectOpen?: () => boolean;
    getInputNode?: any | null;
    getValue?: () => any;
    getEditorProps?: () => any;
    validate?: (value?: any) => boolean;
  };

  constructor(props) {
    super(props);

    this.setEditorRef = (c) => (this.editorRef = c);

    this.createEditor = this.createEditor.bind(this);

    this.state = {
      isInvalid: false,
    };
  }

  componentDidMount() {
    const inputNode = this.getInputNode();
    // inputNode could be undefined OR null.
    if (inputNode !== undefined && inputNode !== null) {
      this.setTextInputFocus(inputNode);
      if (!this.getEditor()?.disableContainerStyles) {
        inputNode.className += ' editor-main';
        if (inputNode.style) {
          inputNode.style.height = `${this.props.height - 1}px`;
        }
      }
    }
  }

  componentWillUnmount() {
    if (!this.changeCommitted && !this.changeCanceled && !this.getEditor()?.ignore) {
      this.commit({ key: 'Enter' });
    }
  }

  isKeyExplicitlyHandled = (key: string): boolean => {
    return isFunction(this[`onPress${key}`]);
  };

  checkAndCall = (methodName: string, args: any) => {
    if (isFunction(this[methodName])) {
      this[methodName](args);
    }
  };

  onKeyDown = (e: any) => {
    if (isCtrlKeyHeldDown(e)) {
      this.checkAndCall('onPressKeyWithCtrl', e);
    } else if (isShiftKeyHeldDown(e)) {
      //Slightly different to the ctrl one - This seems more natural.
      this.checkAndCall(`onPressShift${e.key}`, e);
    } else if (this.isKeyExplicitlyHandled(e.key)) {
      // break up individual keyPress events to have their own specific callbacks
      const callBack = `onPress${e.key}`;
      this.checkAndCall(callBack, e);
    } else if (isKeyPrintable(e.keyCode)) {
      this.checkAndCall('onPressChar', e);
    }

    // Track which keys are currently down for shift clicking etc
    this._keysDown = this._keysDown || {};
    this._keysDown[e.keyCode] = true;
    if (isFunction(this.props.onGridKeyDown)) {
      this.props.onGridKeyDown(e);
    }
  };

  createEditor() {
    const editorProps: EditorProps = {
      ref: this.setEditorRef,
      column: this.props.column,
      value: this.getInitialValue(),
      onCommit: this.commit,
      onCommitCancel: this.commitCancel,
      rowMetaData: this.getRowMetaData(),
      rowData: this.props.rowData,
      height: this.props.height,
      onBlur: this.commit,
      onOverrideKeyDown: this.onKeyDown,
      testId: this.props.testId,
      updateCellState: this.props.updateCellState,
      modalOpen: this.props.modalOpen,
      rowIdx: this.props.rowIdx,
    };

    const CustomEditor = this.props.column.editor;

    // return custom column editor or SimpleEditor if none specified
    if (React.isValidElement(CustomEditor)) {
      return React.cloneElement(CustomEditor, editorProps);
    }
    if (isFunction(CustomEditor) || isReactMemoOrLazy(CustomEditor)) {
      return <CustomEditor {...editorProps} />;
    }
    return (
      <SimpleTextEditor
        ref={this.setEditorRef}
        column={this.props.column}
        value={this.getInitialValue()}
        onBlur={this.commit}
        rowMetaData={this.getRowMetaData()}
        onKeyDown={() => {}}
        commit={() => {}}
      />
    );
  }

  onPressEnter = () => {
    this.commit({ key: 'Enter' });
  };

  onPressShiftEnter = () => {
    this.commit({ key: 'Enter', shift: true });
  };

  onPressTab = () => {
    //this.commit({ key: 'Tab' });
  };

  onPressEscape = (e: any) => {
    if (!this.editorIsSelectOpen()) {
      this.commitCancel();
    } else {
      // prevent event from bubbling if editor has results to select
      e.stopPropagation();
    }
  };

  onPressArrowDown = (e: any) => {
    if (this.editorHasResults()) {
      // dont want to propagate as that then moves us round the grid
      e.stopPropagation();
    } else {
      this.commit(e);
    }
  };

  onPressArrowUp = (e: any) => {
    if (this.editorHasResults()) {
      // dont want to propagate as that then moves us round the grid
      e.stopPropagation();
    } else {
      this.commit(e);
    }
  };

  onPressArrowLeft = (e: any) => {
    // prevent event propagation. this disables left cell navigation
    if (!this.isCaretAtBeginningOfInput()) {
      e.stopPropagation();
    } else {
      this.commit(e);
    }
  };

  onPressArrowRight = (e: any) => {
    // prevent event propagation. this disables right cell navigation
    if (!this.isCaretAtEndOfInput()) {
      e.stopPropagation();
    } else {
      this.commit(e);
    }
  };

  editorHasResults = (): boolean => {
    if (isFunction(this.getEditor()?.hasResults)) {
      return this.getEditor()?.hasResults();
    }

    return false;
  };

  editorIsSelectOpen = () => {
    if (isFunction(this.getEditor()?.isSelectOpen)) {
      return this.getEditor()!.isSelectOpen!();
    }

    return false;
  };

  getRowMetaData = (): any => {
    // clone row data so editor cannot actually change this
    // convention based method to get corresponding Id or Name of any Name or Id property
    if (typeof this.props.column.getRowMetaData === 'function') {
      return this.props.column.getRowMetaData(this.props.rowData, this.props.column);
    }
  };

  getEditor() {
    return this.editorRef;
  }

  getInputNode = (): any | null => {
    return this.getEditor()?.getInputNode();
  };

  getInitialValue = (): string => {
    const selected = this.props.cellMetaData.selected;
    const keyCode = selected.initialKeyCode;
    if (keyCode === 'Delete' || keyCode === 'Backspace') {
      return '';
    } else if (keyCode === 'Enter') {
      return this.props.value;
    }

    const charKey = String.fromCharCode(keyCode >= 96 && keyCode <= 105 ? keyCode - 48 : keyCode);

    return keyCode ? charKey : this.props.value;
  };

  getContainerClass = () => {
    return joinClasses({
      'has-error': this.state.isInvalid === true,
    });
  };

  //Added 'updated' argument to allow ignoring the 'getEditor().getValue()' sketchiness and access it directly.
  commit = (
    args: { key: string; shift?: boolean },
    { value: updatedValue, skipTabBehaviour, setInactive, triggerTemporarySave }: any = {}
  ) => {
    const opts = args || {};

    const updated = updatedValue || this.getEditor()?.getValue?.();

    let inputValue = false;
    const commitObj = { cellKey: this.props.column.key } as CommitObject;

    const editorProps = this.getEditor()?.getEditorProps?.() || {};
    if (opts.key === 'Enter' && editorProps.managedEditor) {
      return;
    } //Editor manages it's own commit calls which won't have opts.key set.

    const searchTabOffset = skipTabBehaviour ? 0 : getIn(editorProps, 'searchTabOffset');
    const searchEnterOffset = skipTabBehaviour ? 0 : getIn(editorProps, 'searchEnterOffset');

    if (this.props.column?.options && (this.props.column.options.linkedFields || this.props.column.options.valueMap)) {
      // Determines if the selected option was entered manually or was an option from the list
      for (const option of editorProps.options) {
        if (option.value === updated[this.props.column.key]) {
          // Manually entered options are not given a className
          if (option.className) {
            inputValue = true;
            break;
          }
        }
      }
    }

    // Linked fields - updating 2 values from one drop down in a specific use case
    if (this.props.column?.options?.linkedFields) {
      const tempValue = updated[this.props.column.key];

      // If linked fields are in use, then the input value linked to the cell key is purposely set to null
      // and the linked field is updated from the defined value
      updated[this.props.column.options.linkedFields.input] = null;
      updated[this.props.column.options.linkedFields.fromList] = updated[this.props.column.key];

      commitObj.cellKey = this.props.column.options.linkedFields.fromList;
      commitObj.cellKey2 = this.props.column.options.linkedFields.input;

      // If the user has entered a manual option, then its not a selected object and can just be assigned
      if (inputValue) {
        updated[this.props.column.options.linkedFields.input] = tempValue;
        updated[commitObj.cellKey] = null;
      }
    }

    if (this.isNewValueValid(updated)) {
      this.changeCommitted = true;
      commitObj.rowIdx = this.props.rowIdx;
      commitObj.updated = updated;
      commitObj.key = opts.key;
      commitObj.shift = !!opts.shift;
      commitObj.searchTabOffset = searchTabOffset;
      commitObj.searchEnterOffset = searchEnterOffset;
      commitObj.setInactive = setInactive;
      commitObj.triggerTemporarySave = triggerTemporarySave;
      this.props.cellMetaData.onCommit(commitObj);
    }
  };

  commitCancel = () => {
    this.changeCanceled = true;
    this.props.cellMetaData.onCommitCancel();
  };

  isNewValueValid = (value: string): boolean => {
    if (isFunction(this.getEditor()?.validate)) {
      const isValid = !!this.getEditor()?.validate?.(value);
      this.setState({ isInvalid: !isValid });
      return isValid;
    }

    return true;
  };

  setCaretAtEndOfInput = () => {
    const input = this.getInputNode();
    // taken from http://stackoverflow.com/questions/511088/use-javascript-to-place-cursor-at-end-of-text-in-text-input-element
    const txtLength = input.value.length;
    if (input.setSelectionRange) {
      input.setSelectionRange(txtLength, txtLength);
    }
  };

  isCaretAtBeginningOfInput = (): boolean => {
    const inputNode = this.getInputNode();
    return inputNode && inputNode.selectionStart === inputNode.selectionEnd && inputNode.selectionStart === 0;
  };

  isCaretAtEndOfInput = (): boolean => {
    const inputNode = this.getInputNode();
    return (inputNode && inputNode.selectionStart === inputNode.value?.length) ?? 0;
  };

  isBodyClicked = (e) => {
    const relatedTarget = this.getRelatedTarget(e);
    return relatedTarget === null;
  };

  isViewportClicked = (e) => {
    const relatedTarget = this.getRelatedTarget(e);
    return relatedTarget.className.indexOf('react-grid-Viewport') > -1;
  };

  isClickInsideEditor = (e) => {
    const relatedTarget = this.getRelatedTarget(e);

    return (
      e.currentTarget.contains(relatedTarget) ||
      relatedTarget.className.indexOf('editing') > -1 ||
      relatedTarget.className.indexOf('react-grid-Cell') > -1
    );
  };

  getRelatedTarget = (e) => {
    return e.relatedTarget || e.explicitOriginalTarget || document.activeElement; // IE11
  };

  handleRightClick = (e) => {
    e.stopPropagation();
  };

  handleBlur = (e) => {
    e.stopPropagation();
    if (this.isBodyClicked(e)) {
      this.commit(e);
    }

    if (!this.isBodyClicked(e)) {
      // prevent null reference
      if (this.isViewportClicked(e) || !this.isClickInsideEditor(e)) {
        this.commit(e);
      }
    }
  };

  setTextInputFocus = (inputNode) => {
    const selected = this.props.cellMetaData.selected;
    const keyCode = selected.initialKeyCode;
    inputNode?.focus();
    if (inputNode?.tagName === 'INPUT') {
      if (!isKeyPrintable(keyCode)) {
        inputNode?.focus();
        inputNode.select();
      } else {
        inputNode.select();
      }
    }
  };

  renderStatusIcon = () => {
    if (this.state.isInvalid === true) {
      return <span className="glyphicon glyphicon-remove form-control-feedback" />;
    }
  };

  render() {
    return (
      <div
        className={this.getContainerClass()}
        onBlur={this.handleBlur}
        onKeyDown={this.onKeyDown}
        onContextMenu={this.handleRightClick}
      >
        {this.createEditor()}
        {this.renderStatusIcon()}
      </div>
    );
  }
}

export default EditorContainer;
